import {
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridColumnHeaders,
  GridFilterModel,
  GridRowClassNameParams,
} from "@mui/x-data-grid-pro";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ViolatedDevices } from "../hooks/useAlertEvaluation";
import CustomFooter from "./CustomFooter";
import ColumnMenu from "@/components/columnMenu/ColumnMenu";
import { I18nGrid } from "@/components/data/I18nGrid";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";
import { DriverNamesContext } from "@/components/grid/cells/DriverNamesContext";
import CustomToolbar from "@/components/grid/CustomToolbar";
import { NoRowsOverlay } from "@/components/grid/NoRowsOverlay";
import RowDetailCockpit from "@/components/grid/RowDetailCockpit";
import AddInIds from "@/constants/AddInIds";
import { VEHICLE_STATUS } from "@/constants/groupConfig";
import { useAddDeviceHistoryEntry } from "@/hooks/addins/useDeviceHistory";
import { useRemoveAddIn } from "@/hooks/addins/useRemoveAddIn";
import { useUpdateAddIn } from "@/hooks/addins/useUpdateAddIn";
import { useDiagnosticColumns } from "@/hooks/columns/useDiagnosticColumns";
import { useUpdateDevice } from "@/hooks/devices/useUpdateDevice";
import { useGroupsByParent } from "@/hooks/groups/useGroupsByParent";
import { SavedFilterRef, useFocusGridRow } from "@/hooks/useFocusGridRow";
import { useDriverNamesByIds } from "@/hooks/users/useDriverNamesByIds";
import { CustomProperty } from "@/models/CustomProperty";
import { DeviceStatusOverviewData } from "@/models/DeviceStatusOverviewData";
import ParkingAddInData from "@/models/ParkingAddInData";
import { RemainingRangeAddInData } from "@/models/RemainingRangeAddInData";
import VehicleStatusAddInData from "@/models/VehicleStatusAddInData";

export interface DiagnosticMonitorGridProps {
  data?: DeviceStatusOverviewData[];
  deviceCount: number;
  filterModel: GridFilterModel;
  setFilterModel: Dispatch<SetStateAction<GridFilterModel>>;
  violatedDevices?: ViolatedDevices;
  /** Device id flagged by the Battery Runaway alarm — its row pulses until acknowledged. */
  alarmDeviceId?: string | null;
  savedFilterRef?: SavedFilterRef;
}

declare module "@mui/x-data-grid-pro" {
  interface FooterPropsOverrides {
    devicecount: number;
  }
}

export function DiagnosticMonitorGrid({
  data,
  deviceCount,
  filterModel,
  setFilterModel,
  violatedDevices,
  alarmDeviceId,
  savedFilterRef,
}: DiagnosticMonitorGridProps) {
  const { t } = useTranslation();
  const { apiRef, pageType, columns, loading, gridState } = useDataGridSettings();
  const initialColumns = useDiagnosticColumns();
  // One batched lookup for every driver in the rows, read by each DriverCell.
  const driverNames = useDriverNamesByIds(data?.map(row => row.driverId));

  useEffect(() => {
    if (!loading && gridState !== null) {
      apiRef.current?.restoreState(gridState);
      setFilterModel(gridState.filter?.filterModel ?? { items: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridState, initialColumns, loading, setFilterModel, columns]);

  const focusRow = useFocusGridRow(filterModel, "device.name", savedFilterRef);
  const cols = useMemo(() => {
    return [...initialColumns, ...columns];
  }, [initialColumns, columns]);

  const updateVehicleStatus = useUpdateAddIn<VehicleStatusAddInData>(
    AddInIds.vehicleStatus,
    "deviceId",
  );
  const removeVehicleAddIn = useRemoveAddIn<VehicleStatusAddInData>(
    AddInIds.vehicleStatus,
    "deviceId",
  );

  const { addEntry: addHistoryEntry, addClearRemark, addStatusChange } = useAddDeviceHistoryEntry();
  const { data: vehicleStatusGroups = [] } = useGroupsByParent(VEHICLE_STATUS);

  const updateParking = useUpdateAddIn<ParkingAddInData>(AddInIds.parking, "deviceId");
  const removeParking = useRemoveAddIn<ParkingAddInData>(AddInIds.parking, "deviceId");

  const updateRemainingRange = useUpdateAddIn<RemainingRangeAddInData>(
    AddInIds.remainingRange,
    "deviceId",
  );
  const removeRemainingRange = useRemoveAddIn<RemainingRangeAddInData>(
    AddInIds.remainingRange,
    "deviceId",
  );

  const updateDevice = useUpdateDevice();

  const getRowClassName = useCallback(
    (params: GridRowClassNameParams) => {
      if (alarmDeviceId && params.row.device?.id === alarmDeviceId) {
        return "alert-violation--catastrophic";
      }
      const severity = violatedDevices?.get(params.row.device?.id);
      if (!severity) return "";
      return `alert-violation--${severity}`;
    },
    [violatedDevices, alarmDeviceId],
  );

  const grid = (
    <I18nGrid
      apiRef={apiRef}
      rows={data}
      columns={cols}
      getRowId={row => row.id}
      loading={loading}
      initialState={
        gridState
          ? {
              ...gridState,
              pinnedColumns: {
                left: [GRID_DETAIL_PANEL_TOGGLE_FIELD, ...(gridState.pinnedColumns?.left ?? [])],
                right: gridState.pinnedColumns?.right ?? [],
              },
            }
          : { pinnedColumns: { left: [GRID_DETAIL_PANEL_TOGGLE_FIELD] } }
      }
      onPinnedColumnsChange={pinned => {
        const left = pinned.left ?? [];
        if (!left.includes(GRID_DETAIL_PANEL_TOGGLE_FIELD)) {
          apiRef.current?.setPinnedColumns({
            ...pinned,
            left: [GRID_DETAIL_PANEL_TOGGLE_FIELD, ...left],
          });
        }
      }}
      slots={{
        noRowsOverlay: NoRowsOverlay,
        columnHeaders: GridColumnHeaders,
        toolbar: CustomToolbar,
        columnMenu: ColumnMenu,
        footer: CustomFooter,
      }}
      filterModel={filterModel}
      onFilterModelChange={setFilterModel}
      onCellClick={focusRow}
      getRowClassName={getRowClassName}
      processRowUpdate={(updatedRow: DeviceStatusOverviewData & { remarkInput?: string }, row) => {
        const remarkInput = updatedRow.remarkInput?.trim();
        if (remarkInput === "" && updatedRow.latestRemark) {
          addClearRemark(updatedRow.id);
        } else if (remarkInput && remarkInput !== updatedRow.latestRemark) {
          addHistoryEntry(updatedRow.id, remarkInput);
        }

        if (updatedRow.parking !== row.parking) {
          if (updatedRow.parking) {
            updateParking.mutate(updatedRow.parking);
          } else {
            removeParking.mutate({ delSearch: updatedRow.id });
          }
        }

        if (updatedRow.vehicleStatus !== row.vehicleStatus) {
          const { vehicleStatus } = updatedRow;
          const oldGroupId = row.vehicleStatus?.group?.id;
          // The status editor writes the selected group's id into `group`, not a Group.
          const newGroupId = vehicleStatus?.group as unknown as string | undefined;

          if (vehicleStatus && newGroupId && newGroupId !== "__NONE__" && newGroupId !== "") {
            updateVehicleStatus.mutate({
              deviceId: updatedRow.id,
              groupId: newGroupId,
              userId: vehicleStatus.userId,
              dateTime: vehicleStatus.dateTime,
            });
          } else {
            removeVehicleAddIn.mutate({ delSearch: updatedRow.id });
          }

          if (oldGroupId !== newGroupId) {
            const oldStatus = vehicleStatusGroups.find(g => g.id === oldGroupId)?.name;
            const newStatus = vehicleStatusGroups.find(g => g.id === newGroupId)?.name;
            if (oldStatus || newStatus) {
              addStatusChange(
                updatedRow.id,
                `Status: ${oldStatus ?? "---"} → ${newStatus ?? "---"}`,
              );
            }
          }
        }

        if (updatedRow.remainingRange !== row.remainingRange) {
          if (updatedRow.remainingRange) {
            updateRemainingRange.mutate(updatedRow.remainingRange);
          } else {
            removeRemainingRange.mutate({ delSearch: updatedRow.id });
          }
        }

        const oldIgnore =
          (row.device.customProperties as CustomProperty[]).find(
            p => p.property.name === "Mängelmeldung ignorieren",
          )?.value === "true";
        const newIgnore =
          (updatedRow.device.customProperties as CustomProperty[]).find(
            p => p.property.name === "Mängelmeldung ignorieren",
          )?.value === "true";
        if (oldIgnore !== newIgnore) {
          updateDevice.mutate({
            device: updatedRow.device,
            ignoreMaintenance: newIgnore.toString(),
          });
        }
        return updatedRow;
      }}
      onProcessRowUpdateError={(error: any) => console.error(error)}
      slotProps={{
        footer: {
          devicecount: deviceCount ?? 0,
        },
        toolbar: {
          report: {
            title:
              pageType === "statusMonitor"
                ? t("admin.templateManager.statusMonitor")
                : t("admin.templateManager.diagnosticMonitor"),
          },
          exportDisabled: loading,
        },
      }}
      getDetailPanelHeight={() => "auto"}
      getDetailPanelContent={
        pageType === "diagnosticMonitor" ? ({ row }) => <RowDetailCockpit row={row} /> : undefined
      }
      disableRowSelectionOnClick={true}
      pagination
      pageSizeOptions={[10, 25, 50, 100, 1000]}
      sx={{
        "& .MuiDataGrid-detailPanel": {
          overflow: "visible",
        },
        "& .MuiDataGrid-virtualScroller": {
          scrollbarWidth: "auto",
          "&::-webkit-scrollbar": {
            display: "block",
            height: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.3)",
            borderRadius: 4,
          },
        },
        "& .alert-violation--info": {
          borderLeft: "4px solid #2196f3",
        },
        "& .alert-violation--warning": {
          borderLeft: "4px solid #ff9800",
        },
        "& .alert-violation--critical": {
          borderLeft: "4px solid #f44336",
          backgroundColor: "rgba(244, 67, 54, 0.12)",
        },
        "& .alert-violation--catastrophic": {
          borderLeft: "4px solid #b71c1c",
          animation: "brRowPulse 0.8s steps(1, end) infinite",
        },
        "@keyframes brRowPulse": {
          "0%": { backgroundColor: "rgba(244, 67, 54, 0.45)" },
          "50%": { backgroundColor: "rgba(244, 67, 54, 0.05)" },
          "100%": { backgroundColor: "rgba(244, 67, 54, 0.45)" },
        },
      }}
    />
  );

  return <DriverNamesContext.Provider value={driverNames}>{grid}</DriverNamesContext.Provider>;
}
